import { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Base, DotsIcon, GripIcon, PlusIcon } from '@itsrever/design-system'
import { RuleStatus, newRuleKey } from './draft'
import { Option, SearchDropdown } from './pickers'

const {
  Button,
  Card,
  IconButton,
  MenuRoot,
  MenuTrigger,
  MenuPortal,
  MenuPositioner,
  MenuPopup,
  MenuItem,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxList,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxCollection,
  ComboboxItem,
  ComboboxItemDetail
} = Base

const DISABLED_ITEM =
  'data-[disabled]:cursor-default data-[disabled]:opacity-40'

// Ordering, duplication and drag state for an ordered rule list; spread the result into RuleRow.
export function useRuleOrder<R extends { key: string }>(
  rules: R[],
  setRules: (rules: R[]) => void
) {
  const [dragging, setDragging] = useState<string | null>(null)
  const move = (key: string, offset: number) => {
    const from = rules.findIndex((rule) => rule.key === key)
    const to = from + offset
    if (from < 0 || to < 0 || to >= rules.length) return
    // Remove and reinsert, not swap: a drag can skip rows, and the rows in between keep their order.
    const next = [...rules]
    next.splice(to, 0, ...next.splice(from, 1))
    setRules(next)
  }
  return (rule: R, index: number) => ({
    n: index + 1,
    total: rules.length,
    dragging: dragging === rule.key,
    onMove: (offset: number) => move(rule.key, offset),
    onDuplicate: () =>
      setRules([
        ...rules.slice(0, index + 1),
        { ...rule, key: newRuleKey() },
        ...rules.slice(index + 1)
      ]),
    onDragStart: () => setDragging(rule.key),
    onDragOverRow: () => {
      if (!dragging || dragging === rule.key) return
      move(dragging, index - rules.findIndex((r) => r.key === dragging))
    },
    onDragEnd: () => setDragging(null)
  })
}

export function useNewRuleShortcut(enabled: boolean, onAdd: () => void) {
  useEffect(() => {
    if (!enabled) return
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement
      const typing =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      // Menus and pickers keep their own open state; their triggers carry data-popup-open.
      const popupOpen = !!document.querySelector('[data-popup-open]')
      if (
        !typing &&
        !popupOpen &&
        !event.metaKey &&
        !event.ctrlKey &&
        event.key === 'n'
      ) {
        event.preventDefault()
        onAdd()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })
}

export const RuleListFrame: React.FC<{
  // i18n prefix holding the rule list copy, e.g. 'shipping_rules'.
  ns: string
  outcome: { label: string; className: string }
  fallback: ReactNode
  // The test return matched no rule, so the fallback is what applies.
  fallbackHighlighted: boolean
  children: ReactNode
}> = ({ ns, outcome, fallback, fallbackHighlighted, children }) => {
  const { t } = useTranslation()
  return (
    <Card className="overflow-hidden">
      {/* Base.Table's header look; the rows are list items, so it can't be a table head. */}
      <div className="flex items-center gap-4 border-b border-solid border-neutral-200 bg-neutral-100 py-2.5 pl-3 pr-4 text-[0.8125rem] leading-5 text-neutral-600">
        <span className="w-10 text-right">#</span>
        <span className="flex-1">{t(`${ns}.column_when`)}</span>
        <span className={outcome.className}>{outcome.label}</span>
        <span className="w-8" />
      </div>
      <ul>{children}</ul>
      <div
        className={
          'flex min-h-16 items-center gap-4 border-t border-solid border-neutral-200 py-4 pl-3 pr-4 ' +
          (fallbackHighlighted ? 'bg-blue-50/70' : 'bg-neutral-50/60')
        }
      >
        <span className="w-10" />
        <span className="flex-1 text-sm text-neutral-500">
          {t(`${ns}.all_other_returns`)}
        </span>
        {fallback}
        <span className="w-8" />
      </div>
    </Card>
  )
}

interface RuleRowProps extends ReturnType<ReturnType<typeof useRuleOrder>> {
  // i18n prefix holding the rule list copy, e.g. 'shipping_rules'.
  ns: string
  status: RuleStatus
  highlighted: boolean
  dimmed: boolean
  editable: boolean
  // The column on the right: what the rule does.
  outcome: ReactNode
  onDelete: () => void
  children: ReactNode
}

export const RuleRow: React.FC<RuleRowProps> = ({
  ns,
  n,
  total,
  status,
  highlighted,
  dimmed,
  dragging,
  editable,
  outcome,
  onMove,
  onDuplicate,
  onDelete,
  onDragStart,
  onDragOverRow,
  onDragEnd,
  children
}) => {
  const { t } = useTranslation()
  return (
    <li
      draggable={editable}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move'
        onDragStart()
      }}
      onDragOver={(event) => {
        event.preventDefault()
        onDragOverRow()
      }}
      onDragEnd={onDragEnd}
      onKeyDown={(event) => {
        if (!editable || !event.altKey) return
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
        // Popover inputs bubble here through the portal; there ⌥↑/⌥↓ moves the caret.
        const target = event.target as HTMLElement
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return
        event.preventDefault()
        onMove(event.key === 'ArrowUp' ? -1 : 1)
      }}
      aria-label={t(`${ns}.rule_label`, { n })}
      className={
        'group relative flex min-h-16 items-center gap-4 border-b border-solid border-neutral-100 py-4 pl-3 pr-4 transition-colors last:border-b-0 ' +
        (highlighted
          ? 'bg-blue-50/70'
          : dragging
            ? 'bg-neutral-50 opacity-60'
            : 'hover:bg-neutral-50/70') +
        (dimmed ? ' opacity-40' : '')
      }
    >
      {editable && status !== 'saved' ? (
        <span
          title={t(`${ns}.status_${status}`)}
          className={
            'absolute inset-y-2 left-0 w-[3px] rounded-r ' +
            (status === 'new' ? 'bg-primary-base' : 'bg-amber-400')
          }
        />
      ) : null}
      <span className="flex w-10 shrink-0 items-center gap-1 text-neutral-300">
        <span
          className={
            editable
              ? 'cursor-grab opacity-0 group-hover:opacity-100'
              : 'invisible'
          }
        >
          <GripIcon />
        </span>
        <span className="w-5 text-right text-xs tabular-nums text-neutral-400">
          {n}
        </span>
      </span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-2.5">
        {children}
      </div>
      {outcome}
      {editable ? (
        <MenuRoot>
          <MenuTrigger
            render={
              <IconButton
                aria-label={t(`${ns}.rule_actions`, { n })}
                className="opacity-0 focus-visible:opacity-100 group-hover:opacity-100 data-[popup-open]:opacity-100"
              />
            }
          >
            <DotsIcon />
          </MenuTrigger>
          <MenuPortal>
            <MenuPositioner sideOffset={4} align="end">
              <MenuPopup>
                <MenuItem onClick={onDuplicate}>
                  {t(`${ns}.duplicate`)}
                </MenuItem>
                <MenuItem
                  disabled={n === 1}
                  className={DISABLED_ITEM}
                  onClick={() => onMove(-1)}
                >
                  {t(`${ns}.move_up`)}
                </MenuItem>
                <MenuItem
                  disabled={n === total}
                  className={DISABLED_ITEM}
                  onClick={() => onMove(1)}
                >
                  {t(`${ns}.move_down`)}
                </MenuItem>
                <MenuItem className="text-red-500" onClick={onDelete}>
                  {t(`${ns}.delete`)}
                </MenuItem>
              </MenuPopup>
            </MenuPositioner>
          </MenuPortal>
        </MenuRoot>
      ) : (
        <span className="w-8" />
      )}
    </li>
  )
}

export interface FieldGroup {
  label: string
  items: Option[]
}

const matchesField = (field: Option, query: string) =>
  `${field.label} ${field.detail ?? ''}`
    .toLowerCase()
    .includes(query.toLowerCase())

export const AddCondition: React.FC<{
  ns: string
  n: number
  empty: boolean
  groups: FieldGroup[]
  searchLabel: string
  emptyText: string
  width: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onPick: (field: string) => void
}> = ({
  ns,
  n,
  empty,
  groups,
  searchLabel,
  emptyText,
  width,
  open,
  onOpenChange,
  onPick
}) => {
  const { t } = useTranslation()
  return (
    <ComboboxRoot<Option>
      items={groups}
      // Nothing stays picked: each pick adds a condition.
      value={null}
      onValueChange={(field) => field && onPick(field.value)}
      itemToStringLabel={(field) => field.label}
      filter={matchesField}
      autoHighlight
      open={open}
      onOpenChange={onOpenChange}
    >
      <ComboboxTrigger
        aria-label={t(`${ns}.add_condition_to`, { n })}
        render={
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={
              empty
                ? undefined
                : 'opacity-0 focus-visible:opacity-100 group-hover:opacity-100 data-[popup-open]:opacity-100'
            }
          />
        }
      >
        <PlusIcon color="currentColor" className="size-4" />
        {t(empty ? `${ns}.add_condition` : `${ns}.condition`)}
      </ComboboxTrigger>
      <SearchDropdown
        label={searchLabel}
        emptyText={emptyText}
        className={`max-h-96 ${width}`}
      >
        <ComboboxList>
          {(group: FieldGroup) => (
            <ComboboxGroup key={group.label} items={group.items}>
              <ComboboxGroupLabel>{group.label}</ComboboxGroupLabel>
              <ComboboxCollection>
                {(field: Option) => (
                  <ComboboxItem key={field.value} value={field}>
                    <span>
                      {field.label}
                      {field.detail ? (
                        <ComboboxItemDetail>{field.detail}</ComboboxItemDetail>
                      ) : null}
                    </span>
                  </ComboboxItem>
                )}
              </ComboboxCollection>
            </ComboboxGroup>
          )}
        </ComboboxList>
      </SearchDropdown>
    </ComboboxRoot>
  )
}
